import { useDroppable } from "@dnd-kit/core";
import { Button, Collapse, Dropdown, Tooltip, theme } from "antd";
import { ItemType } from "antd/es/menu/interface";
import type { ContentTopic } from "api/config/chalice-types";
import { useEffect } from "react";
import {
  MdDelete,
  MdEdit,
  MdMoreVert,
  MdOutlineAdd,
  MdToggleOn,
} from "react-icons/md";
import { TopicListItem } from "./TopicListItem";

interface CategorySectionProps {
  category: string;
  categoryIdForDnd: string;
  topics: ContentTopic[];
  enabledCount: number;
  totalCount: number;
  canEditBusiness: boolean;
  canGeneratePosts: boolean;
  onTopicAction: (key: string, topic: ContentTopic) => void;
  onCategoryAction: (
    action: string,
    category: string,
    color?: string | null
  ) => void;
  onAddTopic: (category: string | null) => void;
  // Bulk Edit Props
  isBulkEditMode: boolean;
  selectedTopicKeys: Set<string>;
  onTopicSelect: (topicKey: string) => void;
  generateTopicKey: (topic: ContentTopic) => string;
  // Control Collapse state
  activeKey: string[];
  onChange: (keys: string | string[]) => void;
  // Optional category color
  categoryColor?: string | null;
}

export const CategorySection = ({
  category,
  categoryIdForDnd,
  topics,
  enabledCount,
  totalCount,
  canEditBusiness,
  canGeneratePosts,
  onTopicAction,
  onCategoryAction,
  onAddTopic,
  // Bulk Edit Props
  isBulkEditMode,
  selectedTopicKeys,
  onTopicSelect,
  generateTopicKey,
  // Control Collapse state
  activeKey,
  onChange,
  categoryColor,
}: CategorySectionProps) => {
  const { token } = theme.useToken();

  // setNodeRef will be applied to the new outer div
  const { setNodeRef: outerSetNodeRef, isOver } = useDroppable({
    id: categoryIdForDnd,
  });

  // DEBUG: Log isOver status
  useEffect(() => {
    console.log(
      `CategorySection Update: ${categoryIdForDnd} isOver: ${isOver}`
    );
  }, [isOver, categoryIdForDnd]);

  const allEnabled = topics.every((topic) => topic.enabled);
  const allDisabled = topics.every((topic) => !topic.enabled);

  const panelHeader = (
    <div className="flex items-center justify-between w-full gap-2">
      <div className="flex items-start gap-2 pb-1 pt-0">
        {categoryColor && (
          <div
            className="w-2.5 h-2.5 rounded-full shrink-0 mt-[9px]"
            style={{ backgroundColor: categoryColor }}
          />
        )}
        <div className="flex flex-col">
          <span className="text-lg font-semibold leading-normal">
            {category}
          </span>
          <span
            className="text-xs leading-tight"
            style={{ color: token.colorTextSecondary }}
          >
            {enabledCount}/{totalCount} Enabled Topics
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {canEditBusiness && (
          <Tooltip title="Add Topic">
            <Button
              type="text"
              size="small"
              icon={<MdOutlineAdd className="text-antd-colorIcon" size={20} />}
              onClick={(e) => {
                e.stopPropagation(); // Prevent collapse toggle
                onAddTopic(category);
              }}
            />
          </Tooltip>
        )}
        {canEditBusiness && (
          <Dropdown
            menu={(() => {
              // Dynamically build menu items
              const menuItems: ItemType[] = [
                {
                  key: "enable-all",
                  label: "Enable Topics",
                  icon: <MdToggleOn size={18} />,
                  disabled: allEnabled || topics.length === 0,
                },
                {
                  key: "disable-all",
                  label: "Disable Topics",
                  icon: <MdToggleOn size={18} />,
                  disabled: allDisabled || topics.length === 0,
                },
              ];

              // Add edit/delete only for non-Uncategorized
              if (category !== "Uncategorized") {
                menuItems.push(
                  { type: "divider" },
                  {
                    key: "edit",
                    label: "Edit Category",
                    icon: <MdEdit size={18} />,
                  },
                  {
                    key: "delete",
                    label: "Delete Category",
                    icon: <MdDelete size={18} />,
                    danger: true,
                  }
                );
              }

              return {
                items: menuItems,
                onClick: ({ key, domEvent }) => {
                  domEvent.stopPropagation(); // Prevent collapse toggle when clicking dropdown
                  // Pass color only for the 'edit' action
                  if (key === "edit") {
                    onCategoryAction(key, category, categoryColor);
                  } else {
                    onCategoryAction(key, category);
                  }
                },
              };
            })()}
            trigger={["click"]}
          >
            <Button
              type="text"
              size="small"
              icon={<MdMoreVert className="text-antd-colorIcon" size={20} />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        )}
      </div>
    </div>
  );

  // Construct the items array for the Collapse component
  const collapseItems = [
    {
      key: categoryIdForDnd, // Use the DND ID as the key for stability
      label: panelHeader,
      // The content of the panel
      children: (
        <div className={`space-y-2 min-h-[50px] p-4`}>
          {topics.length === 0 ? (
            <p className="text-sm text-center italic">
              Drag topics here or{" "}
              <Button
                type="link"
                size="small"
                className="p-0 h-auto leading-inherit inline align-baseline italic"
                style={{
                  color: token.colorPrimary,
                  textDecoration: "underline",
                  fontStyle: "italic",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onAddTopic(category); // Use category name for onAddTopic
                }}
              >
                create a new one
              </Button>{" "}
              for this category.
            </p>
          ) : (
            [...topics]
              .sort((a, b) => a.title.localeCompare(b.title))
              .map((topic, _index) => {
                const topicKey = generateTopicKey(topic);
                const isSelected = selectedTopicKeys.has(topicKey);
                return (
                  <TopicListItem
                    key={topicKey}
                    id={topicKey}
                    topic={topic}
                    canEditBusiness={canEditBusiness}
                    canGeneratePosts={canGeneratePosts}
                    onTopicAction={onTopicAction}
                    isBulkEditMode={isBulkEditMode}
                    isSelected={isSelected}
                    onSelect={() => onTopicSelect(topicKey)}
                  />
                );
              })
          )}
        </div>
      ),
      // Apply styling based on isOver state to the panel item
      style: {
        // Use colorFillAlter as the base background
        backgroundColor: isOver
          ? token.colorPrimaryBgHover // Reverted
          : token.colorFillAlter, // Reverted
        borderRadius: token.borderRadiusLG, // Apply border radius here if needed
        // border: "none", // Remove default Collapse.Panel border
      },
      className: `transition-colors`,
    },
  ];

  return (
    <div ref={outerSetNodeRef}>
      <Collapse
        className="mb-2 bg-transparent border border-solid border-antd-colorBorder"
        style={{
          borderRadius: token.borderRadiusLG,
          overflow: "hidden",
          backgroundColor: "transparent", // Ensure Collapse itself is transparent
        }}
        activeKey={activeKey}
        onChange={onChange}
        bordered={false} // Set bordered to false as panel style handles it
        items={collapseItems} // Use the items prop
      />
    </div>
  );
};
